import React, { useState } from "react";
import { useLanguage } from "../utils/i18n";

// ── Preset Templates ────────────────────────────────────────────────────────

export const PRESET_TEMPLATES = {
  explain: [
    {
      id: "expert_tutor",
      name: "🎓 Expert Tutor",
      description: "Clear, structured explanation with worked examples",
      template: `你是一个老师，用中英文给我解释我输入的概念，举几个例子和类比，加上给8岁小孩儿的解释。

回复模板：

🔍 概念解释（中文 + 英文）

[在此写概念解释]

✨ 例子与类比（中文 + 英文）

[在此写例子与类比]

🧒 给8岁小孩儿的解释（中文 + 英文）

[在此写给小孩儿的解释]`,
    },
  ],

  explainSimple: [
    {
      id: "child_friendly",
      name: "🧒 Super Simple",
      description: "Warm, simple language for any beginner",
      template: `You are explaining to an 8-year-old child. Use ONLY simple words a child knows.
No jargon, no formulas. Short sentences. Be warm, fun, and encouraging.

{concept}

Book context (use only if helpful, simplify everything):
{context}

## 🧒 What Is It?
Explain in 2-3 very simple sentences. Pretend you're talking to a curious 8-year-old.

## 🌟 A Story or Example
Make up a tiny story or everyday example (toys, school, playground, food, animals) that shows the idea.

## 🎉 The Simple Version
One sentence that sums it all up, simple enough to repeat to a friend.`,
    },
    {
      id: "bedtime_story",
      name: "📖 Bedtime Story",
      description: "Explains as a magical, enchanting story",
      template: `You are a bedtime storyteller explaining a concept through a magical story. Make it enchanting, soothing, and memorable.

{concept}

Book context (use only if helpful):
{context}

## 🌙 Once Upon a Time...
Begin a short bedtime story (4-6 sentences) where the concept is the central theme. Use whimsical, gentle language with vivid imagery.

## ✨ The Lesson
In 1-2 sentences, reveal what the story was really teaching.

## 😴 Sweet Dreams Summary
One sentence the reader can carry with them — simple enough to remember when they wake up.`,
    },
    {
      id: "sports_fan",
      name: "⚽ Sports Fan",
      description: "Everything explained through sports and games",
      template: `Explain this concept entirely through sports and games analogies. Use football, basketball, video games, races — whatever fits best.

{concept}

Book context:
{context}

## 🏆 The Game Analogy
Compare the concept to a specific sports rule, game mechanic, or player strategy. Be concrete and specific.

## 📊 The Scoreboard
Break down the key parts of the concept like player stats or a match scoreboard.

## 🎮 How to Win
Explain the practical application like a game strategy or winning move.

## 🥇 The Championship Moment
One sentence: what does truly understanding this concept feel like in the real world?`,
    },
    {
      id: "cooking_recipe",
      name: "🍳 Recipe Style",
      description: "Step-by-step like a cooking recipe",
      template: `Explain this concept like a cooking recipe — with ingredients, method steps, and a finished dish.

{concept}

Book context:
{context}

## 🛒 Ingredients
List the key components or prerequisites of this concept like recipe ingredients.

## 👨‍🍳 Method
Step-by-step instructions explaining how the concept works, structured like cooking steps (Step 1, Step 2...).

## 🍽️ The Finished Dish
What do you get when you put it all together? What does this concept produce or achieve?

## ⭐ Chef's Tip
One insider trick that makes this concept click or easier to remember.`,
    },
  ],

  summary: [
    {
      id: "standard_summary",
      name: "📝 Standard Summary",
      description: "Balanced summary with key points, formulas, and analogy",
      template: `You are an expert tutor. Summarize the following text from pages {start_page}-{end_page} of a book.

Your response MUST include these sections:

## 📝 Summary
A clear, concise summary of the key ideas (3-6 sentences).

## 🔢 Key Formulas
List and explain every important formula or equation found in the text. Write each formula in LaTeX notation (using \\[ ... \\] for display math). If there are no formulas, omit this section.

## 🎯 Key Takeaways
3-6 bullet points of the most important points.

## 🧠 Fun Analogy
2-3 sentences only. A punchy, memorable analogy using everyday objects or situations (pizza, sports, video games, etc.) that makes the core idea click instantly.

## 🇨🇳 中文翻译 (Chinese Translation)
Translate the Summary and Key Takeaways sections above into Simplified Chinese.

TEXT:
{text}`,
    },
    {
      id: "study_notes",
      name: "📚 Study Notes",
      description: "Dense bullet-point notes, perfect for revision",
      template: `Convert this text into dense, exam-ready study notes.

Pages: {start_page}–{end_page}

## 🗂️ Topic Overview
1-2 sentences: what is this section fundamentally about?

## 📋 Key Definitions
List every important term and its definition as a bullet point. Format: **Term** — definition.

## 🔢 Formulas & Equations
All formulas in LaTeX (\\[ ... \\]). Include variable definitions.

## ⚡ Fast-Fact Bullets
A rapid-fire list of 8-12 individual facts worth memorising.

## 🔗 Connections
2-3 ways this content connects to other concepts or real-world applications.

TEXT:
{text}`,
    },
    {
      id: "executive_brief",
      name: "⚡ Executive Brief",
      description: "Ultra-concise — just the essential highlights",
      template: `Give an ultra-concise executive brief. Be ruthlessly brief and high-signal.

Pages {start_page}–{end_page}.

## TL;DR
One sentence. What is this section about?

## Big 3 Points
Exactly 3 bullet points. The most important takeaways — no fluff.

## Key Number or Formula
The single most important figure, statistic, or equation (in LaTeX if a formula). Write "None" if not applicable.

## Bottom Line
One sentence. Why does this content matter?

TEXT:
{text}`,
    },
    {
      id: "story_arc",
      name: "🎭 Story Arc",
      description: "Frames the chapter as a compelling narrative journey",
      template: `Summarize this academic content as a compelling narrative story arc. Make learning feel like reading a story.

Pages {start_page}–{end_page}.

## 🎬 The Setup
What problem or question does this content address? Establish the context and stakes.

## ⚔️ The Conflict / Challenge
What is the core tension, problem, or challenge this section explores or tries to solve?

## 🔬 The Journey
How does the text work through the problem? Describe key ideas as story beats — each one a turning point.

## 🏆 The Resolution
What answer, conclusion, or insight does this content arrive at?

## 🌅 The Lesson
One lasting takeaway phrased as a universal insight or life principle.

TEXT:
{text}`,
    },
    {
      id: "question_based",
      name: "❓ Question & Answer",
      description: "Converts the content into a Q&A format",
      template: `Convert this content into a Q&A format that reveals key ideas through questions and answers.

Pages {start_page}–{end_page}.

## ❓ The Big Questions
List 3-4 big questions this section answers. Number them.

## 🗝️ The Answers
Answer each numbered question in 2-4 sentences, using information directly from the text.

## 🔢 Critical Numbers & Formulas
Any key figures, statistics, or formulas in LaTeX notation.

## 🎯 The Final Test
One synthesis question that someone could only answer correctly if they truly understood all the material.

TEXT:
{text}`,
    },
  ],

  chat: [
    {
      id: "helpful_tutor",
      name: "🎓 Helpful Tutor",
      description: "Balanced, encouraging tutor (default)",
      template: `You are a knowledgeable and encouraging tutor. Answer questions clearly and accurately. Use worked examples when helpful. Format responses with markdown where appropriate.`,
    },
    {
      id: "socratic_guide",
      name: "🤔 Socratic Guide",
      description: "Guides you to answers — never gives them directly",
      template: `You are a Socratic tutor. NEVER give direct answers. Instead, guide the student to find the answer themselves through carefully chosen questions and gentle hints. If the student asks a question, respond with 2-3 probing questions that lead them toward the answer. Only confirm or correct once they've made a genuine attempt.`,
    },
    {
      id: "study_partner",
      name: "👫 Study Partner",
      description: "Casual, peer-like learning companion",
      template: `You are a friendly study partner who happens to know a lot. Keep it casual and conversational — like messaging a smart friend. Use informal language and be encouraging but honest. Share your "opinion" on topics to make it feel like a real conversation rather than a textbook.`,
    },
    {
      id: "devil_advocate",
      name: "😈 Devil's Advocate",
      description: "Challenges your thinking to deepen understanding",
      template: `You are a Devil's Advocate tutor. When a student states something, challenge it with counterarguments, edge cases, or alternative perspectives. Your goal is to deepen understanding by forcing the student to defend and refine their thinking. Be intellectually rigorous but not cruel. End each response with a single challenging follow-up question.`,
    },
    {
      id: "exam_coach",
      name: "📋 Exam Coach",
      description: "Strict, results-focused exam preparation mode",
      template: `You are a strict, results-focused exam coach. Every response must relate to exam success. When answering questions: (1) give the model answer a student should write in an exam, (2) highlight the key mark-winning phrases or steps, (3) warn about common mistakes examiners penalise. If a question is not exam-relevant, redirect to exam strategy. Prioritise precision and technique over friendliness.`,
    },
    {
      id: "feynman_technique",
      name: "🔬 Feynman Technique",
      description: "Explains everything simply, then checks your understanding",
      template: `You use the Feynman Technique: explain everything as simply as possible, as if to someone with no background. When you hit a concept that needs simplifying, say "Let me break that down..." and use a concrete, real-world example. If something can't be simplified, say so and explain why that complexity exists. After each explanation, ask if the student can explain it back in their own words.`,
    },
  ],
};

// ── LocalStorage helpers ─────────────────────────────────────────────────────

const STORAGE_KEY = "promptTemplates";

export function loadTemplates() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) return JSON.parse(saved);
  } catch {}
  return { explain: null, explainSimple: null, summary: null, chat: null };
}

export function saveTemplates(templates) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
}

export function getActiveTemplate(mode) {
  const saved = loadTemplates();
  if (saved[mode]) return saved[mode];
  const presets = PRESET_TEMPLATES[mode];
  return presets ? presets[0].template : "";
}

// ── Modal Component ──────────────────────────────────────────────────────────

const MODE_META = {
  explain: {
    label: "💡 Explain",
    vars: [],
    hint: "写下 AI 应如何回复，概念会自动传入。",
  },
  explainSimple: {
    label: "🧒 Simple Explain",
    vars: [],
    hint: "写下 AI 应如何回复，概念会自动传入。",
  },
  summary: {
    label: "📝 Summary",
    vars: [],
    hint: "自定义 AI 的回复格式和内容结构。",
    fixedPrefix: "You are an expert tutor. Summarize the following text from pages {start_page}-{end_page} of a book.\n\n",
    fixedSuffix: "\n\nTEXT:\n{text}",
  },
  chat: {
    label: "💬 Chat",
    vars: [],
    hint: "This template is used as a system instruction that shapes how the AI behaves throughout the entire conversation.",
  },
};

export default function PromptTemplateModal({ modes, initialMode, onClose }) {
  const { t } = useLanguage();
  const [activeMode, setActiveMode] = useState(initialMode || modes[0]);
  const [drafts, setDrafts] = useState(() => {
    const saved = loadTemplates();
    const result = {};
    modes.forEach((m) => {
      const full = saved[m] || PRESET_TEMPLATES[m][0].template;
      const meta = MODE_META[m];
      if (meta.fixedPrefix && meta.fixedSuffix) {
        let mid = full;
        if (mid.startsWith(meta.fixedPrefix)) mid = mid.slice(meta.fixedPrefix.length);
        if (mid.endsWith(meta.fixedSuffix)) mid = mid.slice(0, mid.length - meta.fixedSuffix.length);
        result[m] = mid;
      } else {
        result[m] = full;
      }
    });
    return result;
  });

  const meta = MODE_META[activeMode];

  const handleReset = () => {
    const full = PRESET_TEMPLATES[activeMode][0].template;
    const meta = MODE_META[activeMode];
    let value = full;
    if (meta.fixedPrefix && meta.fixedSuffix) {
      if (value.startsWith(meta.fixedPrefix)) value = value.slice(meta.fixedPrefix.length);
      if (value.endsWith(meta.fixedSuffix)) value = value.slice(0, value.length - meta.fixedSuffix.length);
    }
    setDrafts((prev) => ({ ...prev, [activeMode]: value }));
  };

  const handleSave = () => {
    const existing = loadTemplates();
    const updated = { ...existing };
    modes.forEach((m) => {
      const meta = MODE_META[m];
      updated[m] = meta.fixedPrefix && meta.fixedSuffix
        ? meta.fixedPrefix + drafts[m] + meta.fixedSuffix
        : drafts[m];
    });
    saveTemplates(updated);
    onClose(true);
  };

  return (
    <div className="pt-overlay" onClick={(e) => e.target === e.currentTarget && onClose(false)}>
      <div className="pt-modal">
        {/* Header */}
        <div className="pt-header">
          <div>
            <h2 className="pt-title">⚙️ {t("ptTitle")}</h2>
            <p className="pt-subtitle">{t("ptSubtitle")}</p>
          </div>
          <button className="pt-close-btn" onClick={() => onClose(false)}>✕</button>
        </div>

        {/* Mode tabs (shown only if multiple modes) */}
        {modes.length > 1 && (
          <div className="pt-mode-tabs">
            {modes.map((m) => (
              <button
                key={m}
                className={`pt-mode-tab ${activeMode === m ? "active" : ""}`}
                onClick={() => setActiveMode(m)}
              >
                {MODE_META[m].label}
              </button>
            ))}
          </div>
        )}

        {/* Body — editor only, no preset sidebar */}
        <div className="pt-body" style={{ display: "flex", flexDirection: "column", padding: "16px 20px", overflow: "hidden" }}>
          <div className="pt-col-label-row" style={{ flexShrink: 0 }}>
            <p className="pt-col-label">{t("ptTemplateEditor")}</p>
            <button className="pt-reset-link" onClick={handleReset}>{t("ptResetDefault")}</button>
          </div>

          <textarea
            className="pt-editor"
            value={drafts[activeMode]}
            onChange={(e) => setDrafts((prev) => ({ ...prev, [activeMode]: e.target.value }))}
            spellCheck={false}
            style={{ flex: 1, minHeight: 0 }}
          />
          {meta.vars.length > 0 && (
            <div className="pt-var-hints">
              <span className="pt-var-label">{t("ptAvailableVars")}</span>
              {meta.vars.map((v) => (
                <code key={v} className="pt-var-chip">{v}</code>
              ))}
              <span className="pt-var-hint-text">— {meta.hint}</span>
            </div>
          )}
          {meta.vars.length === 0 && (
            <p className="pt-var-hint-text" style={{ marginTop: 8 }}>
              💡 {meta.hint}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="pt-footer">
          <button className="pt-btn-cancel" onClick={() => onClose(false)}>{t("ptCancel")}</button>
          <button className="pt-btn-save" onClick={handleSave}>✓ {t("ptSaveApply")}</button>
        </div>
      </div>
    </div>
  );
}
